import { Gauge } from 'lucide-react'
import { CANAL_LIBELLE, euros, instantParis, part, partsRecette, tonVente, ventesParCanal, type Soiree } from './soirees-maison-model.js'

/**
 * LES VENTES D'UNE SOIRÉE — en lecture seule : le panneau « Remplissage » de
 * la colonne de droite, et l'onglet « Ventes & entrées ».
 *
 * La « recette » de la billetterie est la VALEUR FACIALE des billets encore
 * valides, frais exclus : jamais un chiffre d’affaires (§3.5). Sans le droit
 * « Consulter la billetterie en direct », Gestion ne transmet pas la RÉPARTITION par
 * moyen de paiement ; les prix et les compteurs des tarifs restent visibles,
 * il faut les voir pour préparer (décision du 22/10, annexe C point 1).
 * L'écran parle donc de « répartition réservée », jamais de « recette masquée ».
 *
 * Les jauges sont des barres d'une seule teinte (l'accent) sur une piste de
 * la même teinte, plus claire : elles disent une part, jamais une catégorie.
 * L'ambre signale une jauge presque pleine, et le texte le dit toujours.
 */

const nombre = (n: number): string => n.toLocaleString('fr-FR')

/** Une jauge : `valeur` sur `max`. Le texte qui l'accompagne porte le chiffre ; elle, la proportion. */
export function Jauge(props: { valeur: number; max: number; libelle: string; fine?: boolean }) {
  const p = part(props.valeur, props.max)
  const ton = props.max > 0 && p >= 0.9 ? ' bs-jauge--pleine' : ''
  return (
    <span className={`bs-jauge${props.fine ? ' bs-jauge--fine' : ''}${ton}`} role="meter" aria-label={props.libelle}
      aria-valuemin={0} aria-valuemax={Math.max(props.max, 0)} aria-valuenow={Math.min(props.valeur, Math.max(props.max, 0))}>
      <span className="bs-jauge__plein" style={{ inlineSize: `${(p * 100).toFixed(1)}%` }} />
    </span>
  )
}

/* ═══════════════════════ la colonne de droite : le remplissage ═══════════════════════ */

/**
 * LE REMPLISSAGE, comme le suivi d'un événement chez Shotgun : la salle
 * (places occupées sur la jauge), la porte (entrés sur vendus), puis les
 * ventes par canal. Les places occupées comptent aussi un billet annulé dont
 * le porteur est déjà entré : c'est la jauge qui protège la salle.
 */
export function FicheRemplissage({ soiree: s }: { soiree: Soiree }) {
  const c = s.compteurs
  const canaux = ventesParCanal(s)
  const complet = c.jauge_restante === 0 || s.complet_a !== null
  return (
    <section className="lc-panel bs-remplissage" aria-labelledby="bs-remplissage-titre">
      <header className="lc-panel__head">
        <h2 className="lc-section-title bs-titre-icone" id="bs-remplissage-titre"><Gauge size={15} aria-hidden="true" /> Remplissage</h2>
      </header>
      <div className="lc-panel__body bs-remplissage__corps">
        <div className="bs-mesure">
          <div className="bs-mesure__ligne">
            <span className="bs-mesure__libelle">Places occupées</span>
            <span className="bs-mesure__valeur"><strong>{nombre(c.places_occupees)}</strong> / {nombre(s.jauge_totale)}</span>
          </div>
          <Jauge valeur={c.places_occupees} max={s.jauge_totale} libelle="Places occupées sur la jauge" />
          <span className="bs-mesure__aide">
            {complet ? 'Complet : plus aucune place à vendre' : c.jauge_restante === null ? 'Places restantes non rendues par la billetterie'
              : `${nombre(c.jauge_restante)} place${c.jauge_restante > 1 ? 's' : ''} restante${c.jauge_restante > 1 ? 's' : ''}`}
          </span>
        </div>
        <div className="bs-mesure">
          <div className="bs-mesure__ligne">
            <span className="bs-mesure__libelle">Entrés</span>
            <span className="bs-mesure__valeur"><strong>{nombre(c.entres)}</strong> / {nombre(c.vendus)} vendu{c.vendus > 1 ? 's' : ''}</span>
          </div>
          <Jauge valeur={c.entres} max={c.vendus} libelle="Billets entrés sur billets vendus" />
          <span className="bs-mesure__aide">{c.vendus === 0 ? 'Aucun billet vendu pour l’instant' : `${Math.round(part(c.entres, c.vendus) * 100)} % des billets vendus sont passés à l’entrée`}</span>
        </div>
        {canaux.length > 0 ? (
          <div className="bs-canaux">
            <span className="bs-mesure__libelle">Vendus par canal</span>
            <ul className="bs-canaux__liste">
              {canaux.map((k) => (
                <li key={k.canal} className="bs-canal">
                  <span className="bs-canal__nom">{k.libelle}</span>
                  <Jauge valeur={k.vendus} max={c.vendus} libelle={`Billets vendus ${k.libelle.toLowerCase()}, sur l’ensemble des vendus`} fine />
                  <span className="bs-canal__chiffre">{nombre(k.vendus)}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        <p className="lc-hint bs-remplissage__note">Les places occupées comptent aussi un billet annulé dont le porteur est déjà entré : c’est la jauge qui protège la salle.</p>
      </div>
    </section>
  )
}

/* ═══════════════════════ l'onglet « Ventes & entrées » ═══════════════════════ */

export function FicheVentes({ soiree: s, recetteMasquee }: { soiree: Soiree; recetteMasquee: boolean }) {
  const r = s.recette
  const c = s.compteurs
  const totalQuotas = s.tarifs.every((t) => t.quota !== null) ? s.tarifs.reduce((n, t) => n + (t.quota ?? 0), 0) : null
  return (
    <section className="bs-section bs-ventes" aria-labelledby="bs-ventes-titre">
      <h2 className="bs-h2" id="bs-ventes-titre">Ventes &amp; entrées</h2>

      <div className="lc-panel bs-panneau">
        <header className="lc-panel__head bs-panneau__tete">
          <h3 className="lc-section-title">Détail des ventes</h3>
          <span className="lc-hint">Par tarif · « émis » compte aussi les billets annulés</span>
        </header>
        <div className="lc-panel__body lc-panel__body--flush">
          <div className="bs-defile" tabIndex={0} role="region" aria-label="Ventes par tarif">
            <table className="lc-table bs-table">
              <thead><tr><th>Tarif</th><th>État</th><th>Vendus</th><th className="bs-num">Entrés</th><th className="bs-num">Émis</th></tr></thead>
              <tbody>
                {s.tarifs.map((t) => {
                  const ton = tonVente(t.vente_reelle.code)
                  return (
                    <tr key={t.id}>
                      <td>
                        <span className="bs-table__nom">{t.nom}</span>
                        <span className="bs-table__sous">{t.prix_cents === 0 ? (t.exoneration ? 'exonéré' : 'gratuit') : euros(t.prix_cents)} · {CANAL_LIBELLE[t.canal]}</span>
                      </td>
                      <td>
                        {t.vente_reelle.code === 'SOIREE_PAS_EN_VENTE' ? <span className="lc-hint">—</span>
                          : <span className={`lc-pill lc-pill--${ton}`}><span className={`lc-dot${ton === 'idle' ? '' : ` lc-dot--${ton}`}`} aria-hidden="true" />{t.vente_reelle.libelle}</span>}
                      </td>
                      <td>
                        <span className="bs-table__ventes">
                          <span><strong>{nombre(t.compteurs.vendus)}</strong>{t.quota !== null ? ` / ${nombre(t.quota)}` : ' · sans quota'}</span>
                          {t.quota !== null ? <Jauge valeur={t.compteurs.vendus} max={t.quota} libelle={`${t.nom} : vendus sur le quota`} fine /> : null}
                        </span>
                      </td>
                      <td className="bs-num">{nombre(t.compteurs.entres)}</td>
                      <td className="bs-num">{nombre(t.compteurs.emis)}</td>
                    </tr>
                  )
                })}
              </tbody>
              {s.tarifs.length > 0 ? (
                <tfoot>
                  <tr className="bs-table__total">
                    <th scope="row">Total</th>
                    <td />
                    <td><strong>{nombre(c.vendus)}</strong>{totalQuotas !== null ? ` / ${nombre(totalQuotas)}` : ''}</td>
                    <td className="bs-num"><strong>{nombre(c.entres)}</strong></td>
                    <td className="bs-num"><strong>{nombre(s.tarifs.reduce((n, t) => n + t.compteurs.emis, 0))}</strong></td>
                  </tr>
                </tfoot>
              ) : null}
            </table>
          </div>
        </div>
      </div>

      <div className="bs-ventes__bas">
        <div className="lc-panel bs-panneau">
          <header className="lc-panel__head"><h3 className="lc-section-title">Valeur faciale des billets valides</h3></header>
          <div className="lc-panel__body bs-valeur">
            {r ? (
              <>
                <p className="bs-valeur__total"><strong>{euros(r.total_cents)}</strong><span className="lc-hint">frais exclus — pas un chiffre d’affaires</span></p>
                <ul className="bs-valeur__parts">
                  {partsRecette(r).map((x) => (
                    <li key={x.libelle} className="bs-canal">
                      <span className="bs-canal__nom">{x.libelle}</span>
                      <Jauge valeur={x.cents} max={r.total_cents} libelle={`${x.libelle}, part de la valeur faciale`} fine />
                      <span className="bs-canal__chiffre">{euros(x.cents)}</span>
                    </li>
                  ))}
                </ul>
                <p className="lc-hint bs-valeur__gratuits">Billets gratuits : {nombre(r.billets_gratuits)}, comptés à part.</p>
              </>
            ) : recetteMasquee ? (
              <p className="lc-hint">Répartition de la recette (espèces, carte, en ligne) réservée au droit « Consulter la billetterie en direct ». Les prix et les ventes de chaque tarif restent visibles ci-dessus.</p>
            ) : <p className="lc-hint">La billetterie n’a pas rendu de recette pour cette soirée.</p>}
          </div>
        </div>

        <div className="lc-panel bs-panneau">
          <header className="lc-panel__head"><h3 className="lc-section-title">Caisses ouvertes</h3></header>
          <div className="lc-panel__body">
            {s.caisses_ouvertes.length === 0 ? <p className="lc-hint">Aucune pour l’instant : les caisses s’ouvrent à la porte, le soir même.</p> : (
              <ul className="bs-caisses">
                {s.caisses_ouvertes.map((k) => (
                  <li key={`${k.poste}-${k.depuis ?? ''}`}>
                    <strong>{k.poste}</strong> — ouverte depuis le {instantParis(k.depuis)}{k.telephone ? ' · sur un téléphone, qui peut vendre hors ligne : remettre en brouillon ou annuler attend sa fermeture' : ''}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
